import * as math from 'mathjs';
import { DataSeries, Point } from './DataSeries';
import { UNIT_MAP, convertToUnit } from '../utils';
import { PumpProperties } from './PumpProperties';

export class PumpOperationRange {
    private primarySeries: DataSeries;
    private curveSeries: DataSeries | null;
    private pumpProperties: PumpProperties;
    constructor(
        primarySeries: DataSeries,
        curveSeries: DataSeries | null,
        pumpProperties: PumpProperties,
    ) {
        this.primarySeries = primarySeries;
        this.curveSeries = curveSeries;
        this.pumpProperties = pumpProperties;
    }

    private interpolateY(
        xSeries: number[],
        ySeries: number[],
        xValue: number,
    ): number {
        // Interpolates the y value for a given x value
        if (
            !xSeries ||
            !ySeries ||
            xSeries.length === 0 ||
            ySeries.length === 0 ||
            xSeries.length !== ySeries.length
        )
            return Number.NaN;

        //take care of start and end case
        if (xValue < xSeries[0]) return ySeries[0];
        if (xValue > xSeries[xSeries.length - 1])
            return ySeries[ySeries.length - 1];
        //look into the middle
        for (let i = 1; i <= xSeries.length - 1; i++) {
            if (xValue < xSeries[i]) {
                //find the value between i and i-1
                if (xSeries[i] == xSeries[i - 1]) return ySeries[i];
                //linear interpolate the y
                const m =
                    (ySeries[i] - ySeries[i - 1]) /
                    (xSeries[i] - xSeries[i - 1]);
                const b = ySeries[i - 1] - m * xSeries[i - 1];
                return m * xValue + b;
            }
        }
        return 0;
    }
    private findClosestIndex(arr: number[], target: number): number {
        let closestIndex = 0;
        let closestDifference = Math.abs(target - arr[0]);

        for (let i = 1; i < arr.length; i++) {
            const difference = Math.abs(target - arr[i]);
            if (difference < closestDifference) {
                closestIndex = i;
                closestDifference = difference;
            }
        }

        return closestIndex;
    }
    private getApproximateYValue(
        xSeries: number[],
        ySeries: number[],
        xValue: number,
    ): number {
        const exactIdx = xSeries.findIndex((x) => x == xValue);
        if (exactIdx >= 0) return ySeries[exactIdx];
        //look for the value within 2% of the xValue
        const idx = [];
        for (let i = 0; i < xSeries.length; i++) {
            if (xSeries[i] >= xValue * 0.98 && xSeries[i] <= xValue * 1.02)
                idx.push(i);
        }
        //find the closest point
        if (idx.length == 0) {
            idx.push(this.findClosestIndex(xSeries, xValue));
        }
        let sum = 0;
        for (const element of idx) {
            sum += ySeries[element];
        }
        return sum / idx.length;
    }
    public validate(): void {
        if (
            this.pumpProperties.flowRate === undefined ||
            isNaN(this.pumpProperties.flowRate)
        )
            throw new Error('Missing pump property flow rate.');
        if (!this.primarySeries) throw new Error('Invalid primary series');
        if (!this.pumpProperties.flowRateUnit)
            throw new Error('Invalid pump property flow rate unit');
        if (this.curveSeries) {
            if (
                this.curveSeries.assetType != 'pump_curve' &&
                this.curveSeries.assetType != 'head_curve'
            )
                throw new Error('Invalid Curve type');
            if (!this.curveSeries.xSeries || !this.curveSeries.ySeries)
                throw new Error('Invalid Curve X or Y unit');
            if (
                !this.curveSeries.xArray ||
                this.curveSeries.xArray.length === 0
            )
                throw new Error('Invalid Curve X series');
            if (
                !this.curveSeries.yArray ||
                this.curveSeries.yArray.length === 0
            )
                throw new Error('Invalid Curve Y series');
        }
    }
    public calculateOperationValue(xOperationRange: number): Point {
        this.validate();

        let flowRate = this.pumpProperties.flowRate;

        flowRate = (flowRate * xOperationRange) / 100.0;
        if (!this.curveSeries) {
            if (
                this.pumpProperties.flowRateUnit !=
                this.primarySeries.xSeries.unit
            ) {
                const sourceUnit = UNIT_MAP[this.pumpProperties.flowRateUnit]
                    ? UNIT_MAP[this.pumpProperties.flowRateUnit]
                    : this.pumpProperties.flowRateUnit;

                const targetUnit = UNIT_MAP[this.primarySeries.xSeries.unit]
                    ? UNIT_MAP[this.primarySeries.xSeries.unit]
                    : this.primarySeries.xSeries.unit;

                flowRate = convertToUnit(
                    math.unit(flowRate, sourceUnit),
                    targetUnit,
                );
            }
            return {
                x: flowRate,
                y: this.getApproximateYValue(
                    this.primarySeries.xArray,
                    this.primarySeries.yArray,
                    flowRate,
                ),
            };
        } else {
            if (
                this.pumpProperties.flowRateUnit !=
                this.curveSeries.xSeries.unit
            ) {
                const sourceUnit = UNIT_MAP[this.pumpProperties.flowRateUnit]
                    ? UNIT_MAP[this.pumpProperties.flowRateUnit]
                    : this.pumpProperties.flowRateUnit;

                const targetUnit = UNIT_MAP[this.curveSeries.xSeries.unit]
                    ? UNIT_MAP[this.curveSeries.xSeries.unit]
                    : this.curveSeries.xSeries.unit;

                flowRate = convertToUnit(
                    math.unit(flowRate, sourceUnit),
                    targetUnit,
                );
            }
            return {
                x: flowRate,
                y: this.interpolateY(
                    this.curveSeries.xArray,
                    this.curveSeries.yArray,
                    flowRate,
                ),
            };
        }
    }
    public calculateAOR(): { min: Point; max: Point } {
        return {
            min: this.calculateOperationValue(this.pumpProperties.minAOR),
            max: this.calculateOperationValue(this.pumpProperties.maxAOR),
        };
    }
    public calculatePOR(): { min: Point; max: Point } {
        return {
            min: this.calculateOperationValue(this.pumpProperties.minPOR),
            max: this.calculateOperationValue(this.pumpProperties.maxPOR),
        };
    }
}
